import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { autoLogin } from 'src/helpers';
import { getPublicRedirectPathname } from 'src/helpers/utils';
import { useAppDispatch, useAppSelector } from 'src/redux';
import Loader from '../Loader';

interface AuthorizedProps extends PropsWithChildren {
  /** specify who can access this route */
  userTypes?: string[];
  isAdmin?: boolean;
}

const AuthorizedWrapper = ({
  children,
  userTypes,
  isAdmin = false,
}: AuthorizedProps) => {
  const { user, isLoginSuccess } = useAppSelector((state) => state.auth);
  const [isChecking, setIsChecking] = useState(true);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!isLoginSuccess) {
      dispatch(
        autoLogin(() =>
          window.location.replace(
            getPublicRedirectPathname(window.location.pathname),
          ),
        ),
      );
    }
  }, [isLoginSuccess, dispatch]);

  useEffect(() => {
    if (!isLoginSuccess) {
      // autoLogin (above) restores the session on a hard load, or redirects out.
      return;
    }
    // isLoginSuccess can flip true a tick before the user object is hydrated on
    // a hard page load. Wait for a real user before deciding access — otherwise
    // the role check reads is_admin/user_type off an empty {} and wrongly 404s.
    if (!user || !user.id) {
      return;
    }
    if (
      (!isAdmin && !userTypes) ||
      (isAdmin && !user.is_admin) ||
      (userTypes && !userTypes.includes(user.user_type ?? ''))
    ) {
      navigate('/404', { replace: true });
    } else {
      setIsChecking(false);
    }
  }, [user, isLoginSuccess, userTypes, isAdmin]);

  return isChecking ? <Loader /> : <>{children}</>;
};

export default AuthorizedWrapper;
